import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getKpis, type DashboardKpis } from "../../../api/dashboard";
import { ApiClientError } from "../../../api/types";

const DEFAULT_POLL_MS = 30_000;

export interface DashboardKpisQueryResult {
  kpis: DashboardKpis | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  lastUpdatedAt: Date | null;
  refresh: () => void;
}

/**
 * Loads dashboard KPIs on mount, on manual refresh, on tab focus, and on a fixed poll interval.
 * (Workers have no WebSocket push yet — polling matches Mini-style freshness without a manual button.)
 */
export function useDashboardKpisQuery(
  startDate: string,
  endDate: string,
  options?: { pollMs?: number; enabled?: boolean },
): DashboardKpisQueryResult {
  const { t } = useTranslation();
  const pollMs = options?.pollMs ?? DEFAULT_POLL_MS;
  const enabled = options?.enabled !== false;

  const [kpis, setKpis] = useState<DashboardKpis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [tick, setTick] = useState(0);

  const hadDataRef = useRef(false);

  const refresh = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const silent = hadDataRef.current;

    if (silent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    void (async () => {
      try {
        const data = await getKpis(startDate, endDate);
        if (cancelled) return;
        setKpis(data);
        hadDataRef.current = true;
        setLastUpdatedAt(new Date());
      } catch (err) {
        if (cancelled) return;
        setKpis(null);
        setError(
          err instanceof ApiClientError
            ? err.message
            : err instanceof Error
              ? err.message
              : t("dashboard.kpis_load_failed"),
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, startDate, endDate, tick, t]);

  useEffect(() => {
    if (!enabled || pollMs <= 0) return;

    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    }, pollMs);

    return () => window.clearInterval(id);
  }, [enabled, pollMs, refresh]);

  useEffect(() => {
    if (!enabled) return;

    function onVisibility() {
      if (document.visibilityState === "visible") {
        refresh();
      }
    }

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [enabled, refresh]);

  return {
    kpis,
    isLoading,
    isRefreshing,
    error,
    lastUpdatedAt,
    refresh,
  };
}
